import { effectiveBoxUnits } from './bulkReceiveModel';

const numberValue = (value) => Number(value) || 0;

export function shipmentGroupsForPo(po) {
  const groups = new Map();
  for (const box of po?.boxes || []) {
    const shipment = box.shipment || {};
    const key = shipment.key || `${shipment.method || 'NONE'}|${shipment.shipDate || 'NONE'}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        method: null,
        tracking: null,
        shipDate: null,
        deliveredAt: null,
        upsStatus: null,
        arrival: 'not_shipped',
        eta: null,
        boxes: [],
      });
    }
    const group = groups.get(key);
    const firstNonNull = (current, value) => (
      current != null && current !== '' ? current : value != null && value !== '' ? value : null
    );
    group.method = firstNonNull(group.method, shipment.method);
    group.tracking = firstNonNull(group.tracking, shipment.tracking);
    group.shipDate = firstNonNull(group.shipDate, shipment.shipDate);
    group.deliveredAt = firstNonNull(group.deliveredAt, shipment.deliveredAt);
    group.upsStatus = firstNonNull(group.upsStatus, shipment.upsStatus);
    group.eta = firstNonNull(group.eta, shipment.eta);
    group.boxes.push(box);
  }
  return [...groups.values()]
    .map((group) => {
      const hasArrivedBox = group.boxes.some(
        (box) => box.shipment?.arrival === 'arrived' || effectiveBoxUnits(box) > 0,
      );
      const hasInTransitBox = group.boxes.some(
        (box) => box.shipment?.arrival === 'in_transit',
      );
      return {
        ...group,
        arrival: hasArrivedBox ? 'arrived' : hasInTransitBox ? 'in_transit' : 'not_shipped',
      };
    })
    .sort((a, b) => {
      if (a.shipDate == null && b.shipDate != null) return 1;
      if (a.shipDate != null && b.shipDate == null) return -1;
      return String(a.shipDate || '').localeCompare(String(b.shipDate || '')) ||
        a.key.localeCompare(b.key);
    })
    .map((shipment, index) => ({ ...shipment, number: index + 1 }));
}

export function shipmentUnreceivedUnits(shipment) {
  return (shipment?.boxes || []).reduce(
    (sum, box) =>
      sum + Math.max(0, numberValue(box.expectedUnits) - effectiveBoxUnits(box)),
    0,
  );
}

export function getDefaultShipmentKey(po) {
  const arrived = shipmentGroupsForPo(po).filter(
    (shipment) => shipment.arrival === 'arrived' && shipmentUnreceivedUnits(shipment) > 0,
  );
  return arrived.length === 1 ? arrived[0].key : null;
}

export function resolveShipmentKey(po, value) {
  if (!value) return null;
  const normalized = String(value).trim().toUpperCase();
  return (
    shipmentGroupsForPo(po).find(
      (shipment) =>
        shipment.key === value ||
        String(shipment.tracking || '').trim().toUpperCase() === normalized,
    )?.key || null
  );
}

export function shipmentMetadataReady(po) {
  const boxes = po?.boxes || [];
  return (
    boxes.length > 0 &&
    boxes.every((box) => Object.prototype.hasOwnProperty.call(box, 'shipment'))
  );
}

export function findAmbiguousShipmentSkus(po) {
  const openArrived = shipmentGroupsForPo(po).filter(
    (shipment) => shipment.arrival === 'arrived' && shipmentUnreceivedUnits(shipment) > 0,
  );
  const shipmentsBySku = new Map();
  for (const shipment of openArrived) {
    const skus = new Set(
      shipment.boxes
        .filter((box) => numberValue(box.expectedUnits) > effectiveBoxUnits(box))
        .map((box) => box.sku)
        .filter(Boolean),
    );
    for (const sku of skus) {
      if (!shipmentsBySku.has(sku)) shipmentsBySku.set(sku, []);
      shipmentsBySku.get(sku).push(shipment.key);
    }
  }
  return new Map(
    [...shipmentsBySku.entries()].filter(([, shipmentKeys]) => shipmentKeys.length > 1),
  );
}

function sortedBoxes(boxes = []) {
  return [...boxes].sort(
    (a, b) =>
      numberValue(a.boxNumber) - numberValue(b.boxNumber) ||
      String(a.id).localeCompare(String(b.id)),
  );
}

export function buildSkuReceiveRows(po, shipmentKey = null) {
  const shipments = shipmentGroupsForPo(po);
  const scopedShipments = shipmentKey
    ? shipments.filter((shipment) => shipment.key === shipmentKey)
    : shipments.filter(
        (shipment) =>
          shipment.arrival === 'arrived' && shipmentUnreceivedUnits(shipment) > 0,
      );
  const boxesBySku = new Map();
  for (const shipment of scopedShipments) {
    for (const box of shipment.boxes) {
      if (!box.sku) continue;
      if (!boxesBySku.has(box.sku)) boxesBySku.set(box.sku, []);
      boxesBySku.get(box.sku).push(box);
    }
  }
  const lineBySku = new Map((po?.lineItems || []).map((line) => [line.sku, line]));
  const boxesBySkuWholePo = new Map();
  for (const box of po?.boxes || []) {
    if (!box.sku) continue;
    if (!boxesBySkuWholePo.has(box.sku)) boxesBySkuWholePo.set(box.sku, []);
    boxesBySkuWholePo.get(box.sku).push(box);
  }
  const ambiguousSkus = findAmbiguousShipmentSkus(po);

  return [...boxesBySku.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([sku, boxes]) => {
      const orderedBoxes = sortedBoxes(boxes);
      const wholePoBoxes = boxesBySkuWholePo.get(sku) || [];
      const received = orderedBoxes.reduce((sum, box) => sum + effectiveBoxUnits(box), 0);
      const expected = orderedBoxes.reduce((sum, box) => sum + numberValue(box.expectedUnits), 0);
      const scannedWholePo = wholePoBoxes.reduce(
        (sum, box) => sum + numberValue(box.scannedUnits),
        0,
      );
      const savedWholePo = wholePoBoxes.reduce(
        (sum, box) => sum + effectiveBoxUnits(box),
        0,
      );
      return {
        sku,
        productName: lineBySku.get(sku)?.productName || sku,
        boxes: orderedBoxes,
        received,
        expected,
        unreceived: orderedBoxes.reduce(
          (sum, box) =>
            sum + Math.max(0, numberValue(box.expectedUnits) - effectiveBoxUnits(box)),
          0,
        ),
        savedWholePo,
        scannedWholePo,
        tagGap: Math.max(0, savedWholePo - scannedWholePo),
        ambiguousShipmentKeys: ambiguousSkus.get(sku) || [],
      };
    });
}

export function planManualAllocation(skuBoxes, quantity, skuScannedUnits) {
  const amount = Number(quantity);
  if (!Number.isInteger(amount) || amount <= 0) return [];
  const ordered = sortedBoxes(skuBoxes);
  const scannedForSku = skuScannedUnits == null
    ? ordered.reduce((sum, box) => sum + numberValue(box.scannedUnits), 0)
    : numberValue(skuScannedUnits);
  const open = ordered.filter(
    (box) => effectiveBoxUnits(box) < numberValue(box.expectedUnits),
  );
  const allocations = [];
  let remaining = amount;

  for (const box of open) {
    if (remaining <= 0) break;
    const current = effectiveBoxUnits(box);
    const added = Math.min(
      remaining,
      Math.max(0, numberValue(box.expectedUnits) - current),
    );
    if (added <= 0) continue;
    allocations.push({
      id: String(box.id),
      box,
      addedUnits: added,
      targetUnits: current + added,
      action:
        current === 0 && scannedForSku === 0
          ? 'manualReceiveBox'
          : 'overrideReceiveBox',
      allowOverReceipt: false,
    });
    remaining -= added;
  }

  if (remaining > 0 && allocations.length > 0) {
    const last = allocations[allocations.length - 1];
    last.addedUnits += remaining;
    last.targetUnits += remaining;
    last.allowOverReceipt = true;
  } else if (remaining > 0 && ordered.length > 0) {
    const box = ordered[ordered.length - 1];
    const current = effectiveBoxUnits(box);
    allocations.push({
      id: String(box.id),
      box,
      addedUnits: remaining,
      targetUnits: current + remaining,
      action: 'overrideReceiveBox',
      allowOverReceipt: true,
    });
  }

  return allocations;
}

export function splitEpcsAcrossBoxes(skuBoxes, epcs, { allowOverReceipt = false } = {}) {
  const uniqueEpcs = [...new Set((epcs || []).map(String))];
  const ordered = sortedBoxes(skuBoxes);
  const open = ordered.filter(
    (box) => effectiveBoxUnits(box) < numberValue(box.expectedUnits),
  );
  const allocations = [];
  let cursor = 0;

  for (const box of open) {
    if (cursor >= uniqueEpcs.length) break;
    const current = effectiveBoxUnits(box);
    const remaining = Math.max(0, numberValue(box.expectedUnits) - current);
    const chunk = uniqueEpcs.slice(cursor, cursor + remaining);
    if (!chunk.length) continue;
    allocations.push({
      id: String(box.id),
      box,
      epcs: chunk,
      addedUnits: chunk.length,
      targetUnits: current + chunk.length,
      action: 'captureScan',
      allowOverReceipt: false,
    });
    cursor += chunk.length;
  }

  if (cursor < uniqueEpcs.length && allowOverReceipt) {
    const last = allocations[allocations.length - 1];
    const box = last?.box || ordered[ordered.length - 1];
    if (box) {
      if (last) {
        const extras = uniqueEpcs.slice(cursor);
        last.epcs = [...last.epcs, ...extras];
        last.addedUnits += extras.length;
        last.targetUnits += extras.length;
        last.allowOverReceipt = true;
      } else {
        const current = effectiveBoxUnits(box);
        allocations.push({
          id: String(box.id),
          box,
          epcs: uniqueEpcs.slice(cursor),
          addedUnits: uniqueEpcs.length - cursor,
          targetUnits: current + uniqueEpcs.length - cursor,
          action: 'captureScan',
          allowOverReceipt: true,
        });
      }
      cursor = uniqueEpcs.length;
    }
  }

  return {
    allocations,
    unallocatedEpcs: uniqueEpcs.slice(cursor),
  };
}

export function splitValidatedTags({ validation, productIdToSku, targetProductId }) {
  const classification = validation?.classification || {};
  const bySku = new Map();
  const alreadySaved = [
    ...(classification.alreadyInBox || []),
    ...(classification.receivedElsewhere || []),
    ...(classification.receivedBoxUnknown || []),
  ];
  const notOnPo = classification.notOnPo || [];
  const add = (sku, tags) => {
    if (!sku) return;
    if (!bySku.has(sku)) bySku.set(sku, []);
    bySku.get(sku).push(...tags);
  };
  const hex = (tag) => (typeof tag === 'string' ? tag : tag?.hex_epc || tag?.hexEpc);

  add(
    productIdToSku.get(String(targetProductId)) || productIdToSku.get(targetProductId),
    (classification.newlyReceived || validation?.validEpcs || []).map(hex).filter(Boolean),
  );
  for (const tag of classification.newlyReceivedOtherSku || []) {
    const sku =
      productIdToSku.get(String(tag.product_id)) || productIdToSku.get(tag.product_id);
    add(sku, [hex(tag)].filter(Boolean));
  }

  return {
    bySku: new Map(
      [...bySku.entries()].map(([sku, tags]) => [sku, [...new Set(tags)]]),
    ),
    alreadySaved: alreadySaved.map(hex).filter(Boolean),
    notOnPo: notOnPo.map(hex).filter(Boolean),
  };
}

// A SKU has a tag gap when boxes were saved with more units than EPCs (e.g. 100
// counted, 97 tags). Any previously unseen tag of that SKU could then be one of the
// missing tags from an earlier box, and neither read order nor remaining room can
// say which, so every new tag is held for an explicit decision instead of counted.
export function applyLateTagRule({ epcs, unreceived, pendingCount = 0, tagGap = 0 }) {
  if (numberValue(tagGap) > 0) return { counted: [], late: [...epcs], over: [] };
  return { ...splitByRoom({ epcs, unreceived, pendingCount }), late: [] };
}

export function splitByRoom({ epcs, unreceived, pendingCount = 0 }) {
  const room = Math.max(0, numberValue(unreceived) - numberValue(pendingCount));
  return { counted: epcs.slice(0, room), over: epcs.slice(room) };
}

export function hasUnresolvedLateTags(heldTags) {
  return (heldTags || []).some((item) => item.kind === 'late' && item.epcs.length > 0);
}

export function dedupeTags(tags, alreadySeen = []) {
  const seen = new Set(alreadySeen);
  const unique = [];
  const duplicates = [];
  for (const tag of tags || []) {
    if (seen.has(tag)) duplicates.push(tag);
    else {
      seen.add(tag);
      unique.push(tag);
    }
  }
  return { unique, duplicates };
}

export function isShipmentSelectionBlocked(rows, shipmentKey) {
  return !shipmentKey && rows.some((row) => row.ambiguousShipmentKeys.length > 0);
}

export function overReceiptQuantity(quantity, unreceived) {
  return Math.max(0, numberValue(quantity) - numberValue(unreceived));
}

export function retryableSaveItems(items, succeededIds) {
  const succeeded = succeededIds instanceof Set ? succeededIds : new Set(succeededIds || []);
  return (items || []).filter((item) => !succeeded.has(String(item.id)));
}
